import BrandLogo from './BrandLogo';
import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Mail, MapPin, Phone, Instagram } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext.shared';

const TikTokIcon = ({ size = 20, className = "" }: { size?: number, className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
);

const Footer: React.FC = () => {
    const currentYear = new Date().getFullYear();
    const { t } = useLanguage();

    return (
        <footer className="ds-footer bg-surface text-content pt-16 pb-8 border-t border-border">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
                    {/* Brand Column */}
                    <div>
                        <div className="mb-6"><BrandLogo /></div>
                        <div className="mb-6 flex flex-col gap-2">
                            <Link to="/despre-noi" className="text-sm font-bold text-content-secondary hover:text-accent transition-colors flex items-center gap-2">
                                {t('footer.aboutUs')}
                            </Link>
                            <Link to="/prezentare" className="text-sm font-bold text-content-secondary hover:text-accent transition-colors flex items-center gap-2">
                                {t('footer.presentation')}
                            </Link>
                            <Link to="/ghid" className="text-sm font-bold text-accent hover:text-accent-hover transition-colors flex items-center gap-2">
                                Ghidul funcțiilor & Matrice
                            </Link>
                            <Link to="/timeline" className="text-sm font-bold text-content-secondary hover:text-accent transition-colors flex items-center gap-2">
                                {t('footer.timeline')}
                            </Link>
                            <a href="/manual-utilizare.html" className="text-sm font-bold text-content-secondary hover:text-accent transition-colors flex items-center gap-2" target="_blank" rel="noopener noreferrer">
                                {t('footer.userManual')}
                            </a>
                        </div>
                        <p className="text-content-secondary text-sm leading-relaxed sm:leading-normal mb-6">
                            {t('footer.companyDesc')}
                        </p>
                        <div className="flex gap-4">
                            <a href="https://www.facebook.com/profile.php?id=61578803264843" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-content-secondary hover:text-accent transition-colors"><Facebook className="w-5 h-5" /></a>
                            <a href="https://www.instagram.com/legea_aplicata?mdxt=MWZmdTJ2MWsydmd5MQ%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-content-secondary hover:text-accent transition-colors"><Instagram className="w-5 h-5" /></a>
                            <a href="https://www.tiktok.com/@legeaaplicata.ro?is_from_webapp=1&sender_device=pc" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="text-content-secondary hover:text-accent transition-colors"><TikTokIcon className="w-5 h-5" /></a>
                        </div>
                    </div>

                    {/* Conținut și Resurse Juridice */}
                    <div>
                        <h4 className="text-lg font-bold mb-6 font-headings">{t('footer.content')}</h4>
                        <ul className="space-y-3 text-sm text-content-secondary">
                            <li><Link to="/ai-juridic-romania.html" className="hover:text-accent transition-colors font-semibold text-accent">{t('footer.aiSolutions')}</Link></li>
                            <li><Link to="/ghid" className="hover:text-accent transition-colors font-semibold text-accent">Ghidul funcțiilor (45 instrumente)</Link></li>
                            <li><Link to="/modificari-legislative" className="hover:text-accent transition-colors font-semibold text-accent">{t('footer.legislativeChanges')}</Link></li>
                            <li><Link to="/dosare-urmarite" className="hover:text-accent transition-colors">Dosare urmărite & Alerte</Link></li>
                            <li><Link to="/scheme-juridice" className="hover:text-accent transition-colors font-semibold text-accent">{t('footer.legalSchemes')}</Link></li>
                            <li><Link to="/stiri" className="hover:text-accent transition-colors font-semibold text-content">{t('footer.legalArticles')}</Link></li>
                            <li><Link to="/publicare-articole-juridice.html" className="hover:text-accent transition-colors">{t('footer.publishArticles')}</Link></li>
                            <li><Link to="/site-uri-juridice.html" className="hover:text-accent transition-colors">{t('footer.legalWebsites')}</Link></li>
                            <li><Link to="/piete-financiare" className="hover:text-accent transition-colors font-semibold text-accent">{t('footer.financialMarkets')}</Link></li>
                            <li>
                                <Link
                                    to="/video-juridic"
                                    className="hover:text-accent transition-colors flex items-center gap-1.5"
                                >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M8 5v14l11-7z"/>
                                    </svg>
                                    {t('footer.videoJuridic')}
                                </Link>
                            </li>
                            <li><Link to="/perspective-juridice" className="hover:text-accent transition-colors">{t('header.perspectives')}</Link></li>
                            <li><Link to="/practica-neunitara" className="hover:text-accent transition-colors">{t('header.practicaNeunitara')}</Link></li>
                            <li><Link to="/jurnal-practica-judiciara" className="hover:text-accent transition-colors">{t('header.jurnalPracticaJudiciara')}</Link></li>
                        </ul>
                    </div>

                    {/* Legal Resources & Grup */}
                    <div>
                        <h4 className="text-lg font-bold mb-6 font-headings">{t('footer.legalSupport')}</h4>
                        <ul className="space-y-3 text-sm text-content-secondary">
                            <li><Link to="/terms" className="hover:text-accent transition-colors">{t('footer.terms')}</Link></li>
                            <li><Link to="/privacy-policy" className="hover:text-accent transition-colors">{t('footer.privacy')}</Link></li>
                            <li><Link to="/cookies" className="hover:text-accent transition-colors">{t('footer.cookies')}</Link></li>
                            <li><Link to="/gdpr" className="hover:text-accent transition-colors">{t('footer.gdpr')}</Link></li>
                            <li><Link to="/securitate" className="hover:text-accent transition-colors">{t('footer.security')}</Link></li>
                            <li><Link to="/surse-date" className="hover:text-accent transition-colors">{t('footer.sources')}</Link></li>
                            <li><Link to="/politica-retur" className="hover:text-accent transition-colors">{t('footer.returnPolicy')}</Link></li>
                            <li><Link to="/help-center" className="hover:text-accent transition-colors">{t('footer.helpCenter')}</Link></li>
                            <li><Link to="/harta-site" className="hover:text-accent transition-colors">{t('footer.sitemap')}</Link></li>
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h4 id="contact" className="text-lg font-bold mb-6 font-headings">Contact</h4>
                        <ul className="space-y-4 text-sm text-content-secondary">
                            <li className="flex items-start gap-3">
                               <MapPin className="w-5 h-5 text-accent shrink-0" />
                                <span>{t('footer.address')}</span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Phone className="w-5 h-5 text-accent shrink-0" />
                                <span>0751 661 066</span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Mail className="w-5 h-5 text-accent shrink-0" />
                                <span>contact@verdictline.com</span>
                            </li>
                            <li className="pt-2">
                                <Link to="/contact" className="inline-flex items-center justify-center px-6 py-2 bg-brand-gold text-brand-dark font-bold rounded-lg hover:bg-white transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5">
                                    {t('footer.contactUs')}
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-content-muted md:text-content-muted">
                    <div className="flex flex-col md:flex-row items-center gap-4">
                        <p>&copy; {currentYear} LegeaAplicata. {t('footer.rightsReserved')}</p>
                        <p>{t('footer.developedWith')}</p>
                    </div>

                    <div className="flex flex-wrap items-center justify-center gap-4 max-w-full">
                        <a href="https://anpc.ro/ce-este-sal/" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                            <span
                                className="block w-[250px] h-[50px] bg-center bg-contain bg-no-repeat"
                                style={{ backgroundImage: `url('/images/anpc-sal.svg')` }}
                                aria-label="ANPC SAL"
                            />
                        </a>
                        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                            <span
                                className="block w-[250px] h-[50px] bg-center bg-contain bg-no-repeat"
                                style={{ backgroundImage: `url('/images/anpc-sol.svg')` }}
                                aria-label="ANPC SOL"
                            />
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
